import type { AuditPlanDetailDTO } from "@/modules/internal-audit/domain/contracts";
import { formatDate, formatDateTime } from "@/shared/lib/date-time";
import { Badge, Button } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  DownloadIcon,
  EditIcon,
  FileTextIcon,
  HelpCircle,
} from "lucide-react";

type AuditPlanDetailViewProps = {
  auditPlan: AuditPlanDetailDTO;
  onEdit?: () => void;
  onDownloadPDF?: () => void;
  onCreateRecord?: () => void;
  canEdit?: boolean;
  canCreateRecord?: boolean;
  hideHeader?: boolean;
  startTour?: () => void;
};

const statusLabels: Record<string, string> = {
  pending: "ຍັງບໍ່ກວດສອບ",
  in_progress: "ກຳລັງກວດສອບ",
  completed: "ກວດສອບແລ້ວ",
  cancelled: "ຍົກເລີກ",
};

const statusVariants: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  pending: "secondary",
  in_progress: "default",
  completed: "outline",
  cancelled: "destructive",
};

const frequencyLabels: Record<string, string> = {
  monthly: "ປະຈຳເດືອນ",
  quarterly: "ປະຈຳໄຕມາດ",
  semi_annual: "ຄັ້ງຕໍ່ 6 ເດືອນ",
  annual: "ປະຈຳປີ",
  ad_hoc: "ຕາມຄວາມຈຳເປັນ",
};

export function AuditPlanDetailView({
  auditPlan,
  onEdit,
  onDownloadPDF,
  onCreateRecord,
  canEdit,
  canCreateRecord,
  hideHeader,
  startTour,
}: AuditPlanDetailViewProps) {
  const nav = useNavigate({ from: "/app/internal-audit/$id" });

  return (
    <div className="space-y-6">
      {/* Header */}
      {!hideHeader && (
        <div
          className="flex items-start justify-between"
          data-tourid="plan-detail-header"
        >
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-3xl">{auditPlan.name}</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={startTour}
              >
                <HelpCircle className="size-5" />
              </Button>
              {/* <Badge variant={statusVariants[auditPlan.status] || "secondary"}>
                {statusLabels[auditPlan.status] || auditPlan.status}
              </Badge>
              {!auditPlan.isActive && (
                <Badge variant="destructive">ບໍ່ໃຊ້ງານ</Badge>
              )} */}
            </div>
            {auditPlan.description && (
              <p className="mt-2 text-muted-foreground">
                {auditPlan.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() =>
                nav({
                  to: "/app/internal-audit",
                  search: {
                    offset: 0,
                    limit: 20,
                    sort: undefined,
                    filters: undefined,
                  },
                })
              }
            >
              <ArrowLeftIcon className="size-4" />
              ກັບໄປລາຍການ
            </Button>
            {onDownloadPDF && (
              <Button
                variant="outline"
                onClick={onDownloadPDF}
                className="gap-2"
              >
                <DownloadIcon className="h-4 w-4" />
                ເບິ່ງ PDF Preview
              </Button>
            )}
            {canCreateRecord && onCreateRecord && (
              <Button
                variant="outline"
                onClick={onCreateRecord}
                className="gap-2"
              >
                <FileTextIcon className="h-4 w-4" />
                ບັນທຶກການກວດສອບ
              </Button>
            )}
            {canEdit && onEdit && (
              <Button onClick={onEdit}>
                <EditIcon className="h-4 w-4" />
                ແກ້ໄຂ
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Section 1: Basic Info */}
      <section
        className="space-y-3 rounded-lg border bg-card p-4"
        data-tourid="plan-detail-basic-info"
      >
        <h2 className="font-semibold text-lg">ຂໍ້ມູນຜະແນການກວດສອບ</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">ປະເພດການກວດສອບ:</span>
            <p className="font-medium">{auditPlan.auditTypeName || "-"}</p>
          </div>
          <div>
            <span className="text-muted-foreground">ພະແນກ:</span>
            <p className="font-medium">{auditPlan.departmentName || "-"}</p>
          </div>
          <div>
            <span className="text-muted-foreground">ຕໍາແໜ່ງຜູ້ກວດສອບ:</span>
            <p className="font-medium">
              {auditPlan.auditorPositionTitle || "-"}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">ຄວາມຖີ່:</span>
            <p className="font-medium">
              {frequencyLabels[auditPlan.frequency] || auditPlan.frequency}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">ວັນທີກວດສອບຄັ້ງຖັດ:</span>
            <p className="font-medium">
              {auditPlan.nextAuditDate
                ? formatDate(auditPlan.nextAuditDate)
                : "-"}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">ສະຖານະ: </span>
            <Badge variant={statusVariants[auditPlan.status] || "secondary"}>
              {statusLabels[auditPlan.status] || auditPlan.status}
            </Badge>
          </div>
          <div className="col-span-2">
            <span className="text-muted-foreground">ສະຖານະການໃຊ້ງານ: </span>
            <Badge variant={auditPlan.isActive ? "success" : "destructive"}>
              {auditPlan.isActive ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
            </Badge>
          </div>
        </div>
      </section>

      {/* Section 2: Related Items */}
      {(auditPlan.sopName ||
        auditPlan.qcStandardName ||
        auditPlan.checklistTitle ||
        auditPlan.kpiName) && (
        <section className="space-y-3 rounded-lg border bg-card p-4">
          <h2 className="font-semibold text-lg">ລາຍການທີ່ກ່ຽວຂ້ອງ</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {auditPlan.sopName && (
              <div>
                <span className="text-muted-foreground">SOP:</span>
                <p className="font-medium">{auditPlan.sopName}</p>
              </div>
            )}
            {auditPlan.qcStandardName && (
              <div>
                <span className="text-muted-foreground">ມາດຕະຖານ QC:</span>
                <p className="font-medium">{auditPlan.qcStandardName}</p>
              </div>
            )}
            {auditPlan.checklistTitle && (
              <div>
                <span className="text-muted-foreground">
                  Checklist ທີ່ຕ້ອງເຮັດ:
                </span>
                <p className="font-medium">{auditPlan.checklistTitle}</p>
              </div>
            )}
            {auditPlan.kpiName && (
              <div>
                <span className="text-muted-foreground">
                  KPI ທີ່ຜົນການກວດສອບອັບເດດ:
                </span>
                <p className="font-medium">{auditPlan.kpiName}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Section 3: Metadata */}
      <section className="space-y-3 rounded-lg border bg-card p-4">
        <h2 className="font-semibold text-lg">ຂໍ້ມູນເພີ່ມເຕີມ</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">ສ້າງເມື່ອ:</span>
            <p className="font-medium">
              {auditPlan.createdAt ? formatDateTime(auditPlan.createdAt) : "-"}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">ອັບເດດຄັ້ງລ່າສຸດ:</span>
            <p className="font-medium">
              {auditPlan.updatedAt ? formatDateTime(auditPlan.updatedAt) : "-"}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
